import React from 'react';
import {useDocumentTitleFromBreadcrumbs} from 'hooks/useDocumentTitle';
import {
	// Buttons
	IconButton,
	ActionButton,
	PrimaryButton,
	DefaultButton,
	CommandButton,
	CompoundButton,
	MessageBarButton,
	// Layout & Containers
	Panel,
	Stack,
	Separator,
	ScrollablePane,
	findScrollableParent,
	// Dialogs & Overlays
	Dialog,
	DialogFooter,
	Modal,
	Callout,
	TeachingBubble,
	ContextualMenu,
	// Navigation
	Pivot,
	PivotItem,
	Breadcrumb,
	Nav,
	// Form Controls
	TextField,
	Dropdown,
	Toggle,
	Checkbox,
	ChoiceGroup,
	ComboBox,
	SearchBox,
	SpinButton,
	Slider,
	Rating,
	ColorPicker,
	DatePicker,
	TagItem,
	// Pickers
	TagPicker,
	TagPickerBase,
	NormalPeoplePicker,
	PeoplePickerItemSuggestion,
	SwatchColorPicker,
	SuggestionsItem,
	// Data Display
	DetailsList,
	DetailsRow,
	ShimmeredDetailsList,
	Label,
	Text,
	Link,
	Image,
	Icon,
	FontIcon,
	Persona,
	PersonaCoin,
	Facepile,
	MarqueeSelection,
	// Feedback & Status
	MessageBar,
	ProgressIndicator,
	Spinner,
	Shimmer,
	ActivityItem,
	Announced,
	// Utilities
	TooltipHost,
	FocusZone,
	List,
	GroupedList,
	DocumentCard,
	HoverCard,
	Check,
	Selection,
	Sticky,
	// Component Props Interfaces
	IButtonProps,
	IPanelProps,
	IStackProps,
	ISeparatorProps,
	IScrollablePaneProps,
	IDialogProps,
	IDialogFooterProps,
	IModalProps,
	ICalloutProps,
	ITeachingBubbleProps,
	IPivotProps,
	IPivotItemProps,
	IBreadcrumbProps,
	INavProps,
	ITextFieldProps,
	IDragDropContext,
	IDragDropEvents,
	IDropdownProps,
	IToggleProps,
	ICheckboxProps,
	IChoiceGroupProps,
	ISearchBoxProps,
	ISpinButtonProps,
	ISliderProps,
	IRatingProps,
	IColorPickerProps,
	IDatePickerProps,
	ITagItemProps,
	ITagPickerProps,
	IBasePickerProps,
	ISwatchColorPickerProps,
	IDetailsListProps,
	IDetailsRowProps,
	IShimmeredDetailsListProps,
	ILabelProps,
	ITextProps,
	ILinkProps,
	ILinkStyles,
	IImageProps,
	IIconProps,
	IPersonaProps,
	IPersonaCoinProps,
	IFacepileProps,
	IMessageBarProps,
	IProgressIndicatorProps,
	ISpinnerProps,
	IShimmerProps,
	IActivityItemProps,
	ITooltipHostProps,
	IFocusZoneProps,
	IListProps,
	IGroupedListProps,
	IDocumentCardProps,
	IHoverCardProps,
	IDetailsHeaderProps,
	IDetailsRowBaseProps,
	IDialogContentProps,
	IInputProps,
	IPeoplePickerProps,
	IPlainCardProps,
	ITooltipProps,
	IComboBoxProps,
	// Style Interfaces
	IBasePickerStyles,
	IBasePickerStyleProps,
	IButtonStyles,
	ICheckboxStyles,
	IDetailsListStyles,
	IDetailsRowStyles,
	IDropdownStyles,
	IFontIconProps,
	IIconStyles,
	IImageStyles,
	IMessageBarStyles,
	IPanelStyles,
	IScrollablePaneStyles,
	ISpinnerStyles,
	IStackItemStyles,
	IStackStyles,
	IStyle,
	ITextStyles,
	ITooltipStyles,
	ITagItemSuggestionStyles,
	ISeparatorStyles,
	INavStyles,
	INavLink,
	INavLinkGroup,
	// Data/Utility Interfaces
	IBasePickerSuggestionsProps,
	IBreadcrumbItem,
	IChoiceGroupOption,
	IColumn,
	IContextualMenuItem,
	IContextualMenuProps,
	IDatePickerStrings,
	IDetailsList,
	IDropdownOption,
	IGroup,
	IRawStyle,
	IRenderFunction,
	ISelection,
	ISelectionOptions,
	IStickyProps,
	ISuggestionModel,
	ITag,
	PartialTheme,
	Theme,
	ThemeProvider,
	// Enums & Constants
	CheckboxVisibility,
	ConstrainMode,
	ContextualMenuItemType,
	DayOfWeek,
	DetailsListLayoutMode,
	DialogType,
	DirectionalHint,
	FirstWeekOfYear,
	HighContrastSelector,
	HoverCardType,
	MessageBarType,
	PanelType,
	PersonaSize,
	Position,
	SelectionMode,
	SpinnerSize,
	StickyPositionType,
	// Utility Functions
	getGlobalClassNames,
	getInputFocusStyle,
	getPlaceholderStyles,
	hiddenContentStyle,
	initializeIcons,
	memoizeFunction,
	mergeStyles,
	mergeStyleSets,
	styled,
	useTheme,
	setIconOptions,
	getFocusStyle,
	getTheme,
	ICommandBarItemProps,
	ICommandBarProps,
	ICommandBarStyles,
	ITagItemStyles,
	FontWeights,
	ISuggestionItemProps,
	IDetailsColumnRenderTooltipProps,
	IPersona,
	TooltipDelay,
	IComboBoxStyles,
	CommandBarButton,
	TagItemSuggestion,
	ITagItemSuggestionProps,
	IPeoplePickerItemSuggestionProps,
	BasePicker,
	CommandBar,
	ThemeProviderProps,
} from '@fluentui/react';

export {
	mergeStyles as mergeStylesFromMergeStyles,
	mergeStyleSets as mergeStyleSetsFromMergeStyles,
} from '@fluentui/merge-styles';
export type {DeepPartial} from '@fluentui/merge-styles';

/**
 * React 19 compatible wrappers for FluentUI components.
 * FluentUI v8 components return Element type which is incompatible with React 19's ReactNode type.
 * These wrappers ensure type compatibility by casting the return type.
 */

// Button Components
export const IconButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = IconButton as any;
	return <Comp {...props} />;
};

export const ActionButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = ActionButton as any;
	return <Comp {...props} />;
};

export const PrimaryButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = PrimaryButton as any;
	return <Comp {...props} />;
};

export const DefaultButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = DefaultButton as any;
	return <Comp {...props} />;
};

export const CommandButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = CommandButton as any;
	return <Comp {...props} />;
};

export const CompoundButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = CompoundButton as any;
	return <Comp {...props} />;
};

export const MessageBarButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = MessageBarButton as any;
	return <Comp {...props} />;
};

// Layout & Containers
export const PanelCompat: React.FC<IPanelProps> = props => {
	const Comp = Panel as any;
	return <Comp {...props} />;
};

// StackCompat with Stack.Item support
type StackCompatType = React.FC<IStackProps> & {
	Item: typeof Stack.Item;
};

export const StackCompat: StackCompatType = (props => {
	const Comp = Stack as any;
	return <Comp {...props} />;
}) as StackCompatType;
// Preserve Stack.Item as a static property
StackCompat.Item = Stack.Item;

export const SeparatorCompat: React.FC<ISeparatorProps> = props => {
	const Comp = Separator as any;
	return <Comp {...props} />;
};

export const ScrollablePaneCompat: React.FC<IScrollablePaneProps> = props => {
	const Comp = ScrollablePane as any;
	return <Comp {...props} />;
};

// Dialogs & Overlays
export const DialogCompat: React.FC<IDialogProps> = props => {
	const Comp = Dialog as any;
	return <Comp {...props} />;
};

export const DialogFooterCompat: React.FC<IDialogFooterProps> = props => {
	const Comp = DialogFooter as any;
	return <Comp {...props} />;
};

export const ModalCompat: React.FC<IModalProps> = props => {
	const Comp = Modal as any;
	return <Comp {...props} />;
};

export const CalloutCompat: React.FC<ICalloutProps> = props => {
	const Comp = Callout as any;
	return <Comp {...props} />;
};

export const TeachingBubbleCompat: React.FC<ITeachingBubbleProps> = props => {
	const Comp = TeachingBubble as any;
	return <Comp {...props} />;
};

export const ContextualMenuCompat: React.FC<IContextualMenuProps> = props => {
	const Comp = ContextualMenu as any;
	return <Comp {...props} />;
};

// Navigation
export const CommandBarButtonCompat: React.FC<IButtonProps> = props => {
	const Comp = CommandBarButton as any;
	return <Comp {...props} />;
};

export const CommandBarCompat: React.FC<ICommandBarProps> = props => {
	const Comp = CommandBar as any;
	return <Comp {...props} />;
};

export const PivotCompat: React.FC<IPivotProps> = props => {
	const Comp = Pivot as any;
	return <Comp {...props} />;
};

export const BreadcrumbCompat: React.FC<IBreadcrumbProps> = props => {
	// every breadcrumb drives its page's browser tab title.
	useDocumentTitleFromBreadcrumbs(props.items);
	const Comp = Breadcrumb as any;
	return <Comp {...props} />;
};

export const NavCompat: React.FC<INavProps> = props => {
	const Comp = Nav as any;
	return <Comp {...props} />;
};

// Form Controls
export const TextFieldCompat: React.FC<ITextFieldProps> = props => {
	const Comp = TextField as any;
	return <Comp {...props} />;
};

export const DropdownCompat: React.FC<IDropdownProps> = props => {
	const Comp = Dropdown as any;
	return <Comp {...props} />;
};

export const ToggleCompat: React.FC<IToggleProps> = props => {
	const Comp = Toggle as any;
	return <Comp {...props} />;
};

export const CheckboxCompat: React.FC<ICheckboxProps> = props => {
	const Comp = Checkbox as any;
	return <Comp {...props} />;
};

export const ChoiceGroupCompat: React.FC<IChoiceGroupProps> = props => {
	const Comp = ChoiceGroup as any;
	return <Comp {...props} />;
};

export const ComboBoxCompat: React.FC<IComboBoxProps> = props => {
	const Comp = ComboBox as any;
	return <Comp {...props} />;
};

export const SearchBoxCompat: React.FC<ISearchBoxProps> = props => {
	const Comp = SearchBox as any;
	return <Comp {...props} />;
};

export const SpinButtonCompat: React.FC<ISpinButtonProps> = props => {
	const Comp = SpinButton as any;
	return <Comp {...props} />;
};

export const SliderCompat: React.FC<ISliderProps> = props => {
	const Comp = Slider as any;
	return <Comp {...props} />;
};

export const RatingCompat: React.FC<IRatingProps> = props => {
	const Comp = Rating as any;
	return <Comp {...props} />;
};

export const ColorPickerCompat: React.FC<IColorPickerProps> = props => {
	const Comp = ColorPicker as any;
	return <Comp {...props} />;
};

export const BasePickerCompat: React.FC<IBasePickerProps<any>> = props => {
	const Comp = BasePicker as any;
	return <Comp {...props} />;
};

export const DatePickerCompat: React.FC<IDatePickerProps> = props => {
	const Comp = DatePicker as any;
	return <Comp {...props} />;
};

// Pickers
export const TagPickerCompat: React.FC<ITagPickerProps> = props => {
	const Comp = TagPicker as any;
	return <Comp {...props} />;
};

export const TagItemCompat: React.FC<ITagItemProps> = props => {
	const Comp = TagItem as any;
	return <Comp {...props} />;
};

export const TagItemSuggestionCompat: React.FC<
	ITagItemSuggestionProps
> = props => {
	const Comp = TagItemSuggestion as any;
	return <Comp {...props} />;
};

export const TagPickerBaseCompat = TagPickerBase as any;

export const SuggestionsItemCompat: React.FC<
	ISuggestionItemProps<any>
> = props => {
	const Comp = SuggestionsItem as any;
	return <Comp {...props} />;
};

export const PeoplePickerCompat: React.FC<IBasePickerProps<any>> = props => {
	const Comp = NormalPeoplePicker as any;
	return <Comp {...props} />;
};

export const PeoplePickerItemSuggestionCompat: React.FC<
	IPeoplePickerItemSuggestionProps
> = props => {
	const Comp = PeoplePickerItemSuggestion as any;
	return <Comp {...props} />;
};

export const NormalPeoplePickerCompat: React.FC<IPeoplePickerProps> = props => {
	const Comp = NormalPeoplePicker as any;
	return <Comp {...props} />;
};

export const SwatchColorPickerCompat: React.FC<
	ISwatchColorPickerProps
> = props => {
	const Comp = SwatchColorPicker as any;
	return <Comp {...props} />;
};

// Data Display
export const DetailsListCompat: React.FC<IDetailsListProps> = props => {
	const Comp = DetailsList as any;
	return <Comp {...props} />;
};

export const DetailsRowCompat: React.FC<IDetailsRowProps> = props => {
	const Comp = DetailsRow as any;
	return <Comp {...props} />;
};

export const ShimmeredDetailsListCompat: React.FC<
	IShimmeredDetailsListProps
> = props => {
	const Comp = ShimmeredDetailsList as any;
	return <Comp {...props} />;
};

export const LabelCompat: React.FC<ILabelProps> = props => {
	const Comp = Label as any;
	return <Comp {...props} />;
};

export const TextCompat: React.FC<ITextProps> = props => {
	const Comp = Text as any;
	return <Comp {...props} />;
};

export const LinkCompat: React.FC<ILinkProps> = props => {
	const Comp = Link as any;
	return <Comp {...props} />;
};

export const ImageCompat: React.FC<IImageProps> = props => {
	const Comp = Image as any;
	return <Comp {...props} />;
};

export const IconCompat: React.FC<IIconProps> = props => {
	const Comp = Icon as any;
	return <Comp {...props} />;
};

export const FontIconCompat: React.FC<IIconProps> = props => {
	const Comp = FontIcon as any;
	return <Comp {...props} />;
};

export const PersonaCompat: React.FC<IPersonaProps> = props => {
	const Comp = Persona as any;
	return <Comp {...props} />;
};

export const PersonaCoinCompat: React.FC<IPersonaCoinProps> = props => {
	const Comp = PersonaCoin as any;
	return <Comp {...props} />;
};

export const FacepileCompat: React.FC<IFacepileProps> = props => {
	const Comp = Facepile as any;
	return <Comp {...props} />;
};

export const MarqueeSelectionCompat: React.FC<any> = props => {
	const Comp = MarqueeSelection as any;
	return <Comp {...props} />;
};

// Feedback & Status
export const MessageBarCompat: React.FC<IMessageBarProps> = props => {
	const Comp = MessageBar as any;
	return <Comp {...props} />;
};

export const ProgressIndicatorCompat: React.FC<
	IProgressIndicatorProps
> = props => {
	const Comp = ProgressIndicator as any;
	return <Comp {...props} />;
};

export const SpinnerCompat: React.FC<ISpinnerProps> = props => {
	const Comp = Spinner as any;
	return <Comp {...props} />;
};

export const ShimmerCompat: React.FC<IShimmerProps> = props => {
	const Comp = Shimmer as any;
	return <Comp {...props} />;
};

export const AnnouncedCompat: React.FC<any> = props => {
	const Comp = Announced as any;
	return <Comp {...props} />;
};

export const ActivityItemCompat: React.FC<IActivityItemProps> = props => {
	const Comp = ActivityItem as any;
	return <Comp {...props} />;
};

// Utilities
export const TooltipHostCompat: React.FC<ITooltipHostProps> = props => {
	const Comp = TooltipHost as any;
	return <Comp {...props} />;
};

export const StickyCompat: React.FC<IStickyProps> = props => {
	const Comp = Sticky as any;
	return <Comp {...props} />;
};

export const FocusZoneCompat: React.FC<IFocusZoneProps> = props => {
	const Comp = FocusZone as any;
	return <Comp {...props} />;
};

export const ListCompat: React.FC<IListProps> = props => {
	const Comp = List as any;
	return <Comp {...props} />;
};

export const GroupedListCompat: React.FC<IGroupedListProps> = props => {
	const Comp = GroupedList as any;
	return <Comp {...props} />;
};

export const DocumentCardCompat: React.FC<IDocumentCardProps> = props => {
	const Comp = DocumentCard as any;
	return <Comp {...props} />;
};

export const CheckCompat: React.FC<any> = props => {
	const Comp = Check as any;
	return <Comp {...props} />;
};

export const ThemeProviderCompat: React.FC<ThemeProviderProps> = props => {
	const Comp = ThemeProvider as any;
	return <Comp {...props} />;
};

// Re-export Types and Interfaces
export type {
	IButtonProps,
	IPanelProps,
	IStackProps,
	ISeparatorProps,
	IScrollablePaneProps,
	IDialogProps,
	IDialogFooterProps,
	IModalProps,
	ICalloutProps,
	ITeachingBubbleProps,
	IDetailsColumnRenderTooltipProps,
	IPivotProps,
	IPivotItemProps,
	IBreadcrumbProps,
	INavProps,
	ITextFieldProps,
	IDropdownProps,
	IDragDropContext,
	IDragDropEvents,
	IToggleProps,
	ICheckboxProps,
	IChoiceGroupProps,
	ISearchBoxProps,
	ISpinButtonProps,
	ISliderProps,
	IRatingProps,
	IColorPickerProps,
	IDatePickerProps,
	ITagPickerProps,
	IBasePickerProps,
	ITagItemProps,
	ITagItemStyles,
	ISwatchColorPickerProps,
	IDetailsListProps,
	IDetailsRowProps,
	IShimmeredDetailsListProps,
	ILabelProps,
	ITextProps,
	ILinkProps,
	ILinkStyles,
	IImageProps,
	IIconProps,
	IPersonaProps,
	IPersonaCoinProps,
	IFacepileProps,
	IMessageBarProps,
	IProgressIndicatorProps,
	ISpinnerProps,
	IShimmerProps,
	IActivityItemProps,
	ITooltipHostProps,
	IFocusZoneProps,
	IListProps,
	IGroupedListProps,
	IDocumentCardProps,
	IHoverCardProps,
	IDetailsHeaderProps,
	IDetailsRowBaseProps,
	IDialogContentProps,
	IInputProps,
	IPeoplePickerProps,
	IPlainCardProps,
	ITooltipProps,
	IComboBoxProps,
	// Style Interfaces
	IBasePickerStyles,
	IBasePickerStyleProps,
	IButtonStyles,
	ICheckboxStyles,
	IDetailsListStyles,
	IDetailsRowStyles,
	IDropdownStyles,
	IFontIconProps,
	IIconStyles,
	IImageStyles,
	IMessageBarStyles,
	IModalStyles,
	IPanelStyles,
	IScrollablePaneStyles,
	ISpinnerStyles,
	IStackItemStyles,
	IStackStyles,
	IStyle,
	ITextStyles,
	ITooltipStyles,
	ITagItemSuggestionStyles,
	ISeparatorStyles,
	INavStyles,
	INavLink,
	INavLinkGroup,
	// Data/Utility Interfaces
	IBasePickerSuggestionsProps,
	IBasePicker,
	IBreadcrumbItem,
	IChoiceGroupOption,
	IColumn,
	IContextualMenuItem,
	IContextualMenuProps,
	IDatePickerStrings,
	IDetailsList,
	IDropdownOption,
	IGroup,
	IRawStyle,
	IRenderFunction,
	ISelection,
	ISelectionOptions,
	IStickyProps,
	ISuggestionModel,
	ISuggestionItemProps,
	ICommandBarItemProps,
	ICommandBarProps,
	ICommandBarStyles,
	ITag,
	PartialTheme,
	Theme,
	IComboBoxStyles,
} from '@fluentui/react';

// Re-export Enums and Constants
export {
	CheckboxVisibility,
	ConstrainMode,
	ContextualMenuItemType,
	DayOfWeek,
	DetailsListLayoutMode,
	DialogType,
	DirectionalHint,
	FirstWeekOfYear,
	HighContrastSelector,
	HoverCardType,
	MessageBarType,
	PanelType,
	PersonaSize,
	Position,
	SelectionMode,
	SpinnerSize,
	StickyPositionType,
	TooltipDelay,
	PivotItem,
};

// Re-export Utility Functions
export {
	getGlobalClassNames,
	getInputFocusStyle,
	getPlaceholderStyles,
	hiddenContentStyle,
	initializeIcons,
	memoizeFunction,
	mergeStyles,
	mergeStyleSets,
	styled,
	useTheme,
	setIconOptions,
	getFocusStyle,
	getTheme,
	findScrollableParent,
	FontWeights,
};

// Re-export Selection utility
export {Selection};

export const HoverCardCompat: React.FC<IHoverCardProps> = props => {
	const Comp = HoverCard as any;
	return <Comp {...props} />;
};

// Add missing exports:
export type {IDetailsListCheckboxProps} from '@fluentui/react';
export type {IStackTokens} from '@fluentui/react';
export type {IPersona} from '@fluentui/react';
